import React from 'react';
import { Check, Sparkles, X, Info, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { StorageCalculator } from '../components/StorageCalculator';

const Pricing = ({ user, onSignIn, isAdmin }: { user?: any, onSignIn?: () => void, isAdmin?: boolean }) => {
  const { t, isBrazil, region, currencySymbol, setRegion } = useTranslation();
  
  const regions = [
    { 
      id: 'za', name: t.southAfrica, currency: 'R', 
      solo_mo: 250, solo_yr: 2400,
      growth_mo: 550, growth_yr: 5500,
      agency_mo: 1450, agency_yr: 13500,
      discount: 100 
    },
    { 
      id: 'us', name: t.unitedStates, currency: '$', 
      solo_mo: 15, solo_yr: 145,
      growth_mo: 34, growth_yr: 330,
      agency_mo: 85, agency_yr: 900,
      discount: 6 
    },
    { 
      id: 'uk', name: t.unitedKingdom, currency: '£', 
      solo_mo: 12, solo_yr: 109,
      growth_mo: 29, growth_yr: 250,
      agency_mo: 69, agency_yr: 650,
      discount: 4.8 
    },
    { 
      id: 'br', name: t.brazil, currency: 'R$', 
      solo_mo: 55, solo_yr: 750,
      growth_mo: 129, growth_yr: 1200,
      agency_mo: 299, agency_yr: 2999,
      discount: 30 
    }
  ];

  const currentRegion = regions.find(r => r.id === region) || regions[1]; // Default to US if not found
  const [promoCode, setPromoCode] = React.useState('');
  const isPromoValid = promoCode.toUpperCase() === 'WELCOME2026';

  const getPriceContent = (planId: 'solo' | 'growth' | 'agency', isPopular: boolean) => {
    const basePrice = (currentRegion as any)[`${planId}_mo`];
    const symbol = currentRegion.currency;
    const formatted = `${symbol}${basePrice}`;

    if (isPromoValid) {
      const discounted = Math.max(0, basePrice - currentRegion.discount);
      const promoText = t.appliedPromo;
      return (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <span className={`text-xl line-through ${isPopular ? 'text-indigo-200' : 'text-gray-400'}`}>{formatted}</span>
            <span className="text-4xl font-black">{symbol}{discounted.toFixed(discounted % 1 === 0 ? 0 : 2)}</span>
          </div>
          <div className={`text-[10px] font-bold mt-1 ${isPopular ? 'text-yellow-300' : 'text-indigo-600'}`}>{promoText}</div>
        </div>
      );
    }

    return <div className="text-4xl font-black">{formatted}</div>;
  };

  const plans = [
    {
      id: 'solo' as const,
      name: "Solo",
      desc: t.soloDesc,
      features: t.plSoloFeatures,
      cta: t.startFree,
      popular: false
    },
    {
      id: 'growth' as const,
      name: "Growth",
      desc: t.growthDesc,
      features: t.plGrowthFeatures,
      cta: t.goGrowth,
      popular: true
    },
    {
      id: 'agency' as const,
      name: "Agency",
      desc: t.agencyDesc,
      features: t.plAgencyFeatures,
      cta: t.contactSales,
      popular: false
    }
  ];

  return (
    <div className="pt-24 pb-24 bg-[#030303] min-h-screen">
      <SEO 
        title={`${t.pricing} - PlataPost`}
        description={t.pricingSubtitle}
      />

      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20 space-y-4">
          <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm">
            {t.pricing}
          </p>
          <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight tracking-tighter">
            {t.simplePricing.split(' ')[0]} <span className="text-indigo-500">{t.simplePricing.split(' ').slice(1).join(' ')}</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-medium">
            {t.pricingSubtitle}
          </p>

          <div className="flex flex-col items-center gap-8 pt-8">
            {isAdmin && (
              <div className="flex flex-col items-center gap-2">
                <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  {t.selectRegion}
                </label>
                <div className="inline-flex items-center gap-2 p-1.5 bg-white/5 rounded-2xl border border-white/10">
                  {regions.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRegion(r.id as any)}
                      className={`px-4 py-2 text-sm font-bold rounded-xl transition-all ${
                        region === r.id
                          ? 'bg-white text-indigo-600 shadow-sm'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {r.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <label className="text-xs font-black text-gray-500 uppercase tracking-widest">
                {t.promoCodeLabel}
              </label>
              <div className="relative">
                <input 
                  type="text"
                  placeholder="WELCOME2026"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  className="px-6 py-3 bg-white/5 border border-white/10 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all uppercase placeholder:text-gray-600 w-64 text-center text-white"
                />
                {isPromoValid && (
                  <div className="absolute -top-2 -right-2 bg-green-500 text-white p-1 rounded-full shadow-lg">
                    <Check className="w-3 h-3" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-32">
          {plans.map((plan) => (
            <div 
              key={plan.name}
              className={`relative rounded-[3.5rem] p-10 border transition-all hover:-translate-y-2 duration-300 ${
                plan.popular 
                  ? 'bg-indigo-600 text-white border-indigo-600 shadow-2xl shadow-indigo-500/20' 
                  : 'bg-white/5 text-white border-white/10 shadow-sm'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-yellow-400 text-gray-900 text-xs font-black uppercase tracking-widest rounded-full">
                  {t.mostPopular}
                </div>
              )}
              
              <div className="mb-8">
                <h3 className={`text-2xl font-black mb-2 opacity-100`}>{plan.name}</h3>
                <p className={`text-sm ${plan.popular ? 'text-indigo-100' : 'text-gray-400'}`}>{plan.desc}</p>
              </div>

              <div className="mb-8 h-20 flex items-center">
                <div className="flex items-baseline gap-1">
                  {getPriceContent(plan.id, plan.popular || false)}
                  <span className={`text-sm font-bold ${plan.popular ? 'text-indigo-200' : 'text-gray-500'}`}>
                    {t.perMonth}
                  </span>
                </div>
              </div>

              <ul className="space-y-4 mb-10 min-h-[300px]">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${plan.popular ? 'bg-white/20' : 'bg-indigo-50/10'}`}>
                      <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-indigo-400'}`} />
                    </div>
                    <span className="text-sm font-medium">{feature}</span>
                  </li>
                ))}
              </ul>

              {user && plan.id !== 'solo' ? (
                <Link 
                  to="/dashboard"
                  className={`w-full py-4 rounded-2xl font-black text-center transition-all flex items-center justify-center ${
                    plan.popular 
                      ? 'bg-white text-indigo-600 hover:bg-gray-50' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20'
                  }`}
                >
                  {plan.cta}
                </Link>
              ) : (
                <Link 
                  to="/booking"
                  className={`w-full py-4 rounded-2xl font-black text-center transition-all flex items-center justify-center ${
                    plan.popular 
                      ? 'bg-white text-indigo-600 hover:bg-gray-50' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-600/20'
                  }`}
                >
                  {plan.cta}
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Storage Footprint & Calculator */}
        {(user?.email === 'dewaldjordaan5@gmail.com' || isAdmin) && (
          <div className="max-w-5xl mx-auto my-20">
            <StorageCalculator />
          </div>
        )}

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="text-center">
             <p className="text-indigo-500 font-bold uppercase tracking-widest text-sm mb-4">{t.faqTitle}</p>
             <h2 className="text-4xl font-black text-white tracking-tighter">{t.pricing} FAQ</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-8 rounded-3xl border border-white/5 space-y-3">
              <h4 className="text-lg font-black text-white">{t.canIChangePlans}</h4>
              <p className="text-gray-400 text-sm leading-relaxed font-medium">{t.canIChangePlansAns}</p>
            </div>
            <div className="glass-card p-8 rounded-3xl border border-white/5 space-y-3">
              <h4 className="text-lg font-black text-white">{t.isThereAFreeTrialPricing}</h4>
              <p className="text-gray-400 text-sm leading-relaxed font-medium">{t.isThereAFreeTrialPricingAns}</p>
            </div>
          </div>

          <div className="text-center pt-8">
             <Link to="/faq" className="inline-flex items-center gap-2 text-indigo-400 font-bold hover:text-indigo-300 transition-colors">
                <span>{t.viewAllQuestions}</span>
                <ArrowRight className="w-4 h-4 ml-2" />
             </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
